import { forwardRef } from "react";

import { Box, BoxProps, useTheme } from "@mui/material";

import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";

export type FormIconProps = BoxProps & {
  size?: "xsmall" | "small" | "medium" | "large" | "other";
  disabled?: boolean;
};

export const FormIcon = forwardRef<HTMLDivElement, FormIconProps>(
  ({ sx, children, size = "medium", flexShrink, disabled, ...props }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.global) {
      return <InvalidThemeErrorMessage />;
    }

    const getSize = () => {
      switch (size) {
        case "xsmall":
          return "12px";
        case "small":
          return "16px";
        case "medium":
          return "20px";
        case "large":
          return "24px";
        default:
          return undefined;
      }
    };

    const styles = mergeSx(
      {
        fill: "currentColor",
        width: getSize(),
        height: getSize(),
        transition: "outline 100ms ease-out",
        lineHeight: 0,
        flexShrink: 0,
        outlineColor: "transparent",
        ["&:focus-visible"]: {
          borderRadius: "50%", // This is to make the outline round
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
        ...(disabled && {
          pointerEvents: "none",
          opacity: 0.4,
        }),
      },
      sx
    );

    return (
      <Box flexShrink={flexShrink ?? 0} {...props} sx={styles} ref={ref}>
        {children}
      </Box>
    );
  }
);

FormIcon.displayName = "FormIcon";
